import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icon';
import {
  currencyDisplayName,
  currencySymbol,
  formatAmountInput,
  formatPercentageInput,
  formatQuantityInput,
} from './amountInput';
import { useFieldControlProps } from './field';
import { acquireScrollLock, useDialogKeyboard, useRestoreFocus } from './overlay';
import './styles/overlay-controls.css';
import './styles/button.css';
import './styles/input.css';
import './styles/select.css';
import './styles/card.css';
import './styles/modal.css';
import './styles/segmented.css';

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

export { Field, useField } from './field';
export type { FieldProps } from './field';

/**
 * Core UI primitives on the design system. Every interactive element:
 *  - `--tap` (48px) minimum interactive height; `sm` keeps compact typography/padding,
 *  - theme-token colours only (works light + dark),
 *  - visible focus-visible ring, disabled semantics.
 * Inputs are ≥16px to stop iOS zoom-on-focus.
 */

// ── Button ────────────────────────────────────────────────────────────────
type BtnVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'tertiary' | 'danger' | 'destructive';
export const Button = forwardRef<
  HTMLButtonElement,
  {
    variant?: BtnVariant;
    icon?: IconName;
    loading?: boolean;
    full?: boolean;
    size?: 'md' | 'sm';
  } & ButtonHTMLAttributes<HTMLButtonElement>
>(function Button(
  { variant = 'primary', icon, loading, children, full = false, size = 'md', className, ...rest },
  ref,
) {
  const v = variant === 'destructive' ? 'danger' : variant === 'tertiary' ? 'ghost' : variant;
  return (
    <button
      {...rest}
      ref={ref}
      disabled={rest.disabled || loading}
      className={cx(
        'cev-button',
        `cev-button--${v}`,
        size === 'sm' ? 'cev-button--sm' : 'cev-button--md',
        full && 'cev-button--full',
        className,
      )}
    >
      {loading ? <Spinner /> : icon ? <Icon name={icon} size={size === 'sm' ? 18 : 20} /> : null}
      {children}
    </button>
  );
});

Button.displayName = 'Button';

function Spinner() {
  return <span aria-hidden className="cev-button__spinner" />;
}

export function FileInput({
  accept,
  capture,
  disabled,
  emptyLabel = 'Choose file',
  value,
  onChange,
}: {
  accept?: string;
  capture?: boolean | 'user' | 'environment';
  disabled?: boolean;
  emptyLabel?: string;
  value?: File | null;
  onChange: (file: File | null) => void;
}) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const label = value?.name || emptyLabel;
  return (
    <div className="cev-file-input">
      <input
        ref={inputRef}
        id={id}
        className="sr-only"
        type="file"
        accept={accept}
        capture={capture}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.files?.[0] ?? null);
          event.target.value = '';
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        icon="download"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        {label}
      </Button>
      {value ? (
        <button type="button" className="cev-file-input__clear" disabled={disabled} onClick={() => onChange(null)}>
          Clear
        </button>
      ) : null}
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────
export function Card({
  children,
  padded = true,
  padding = 'md',
  header,
  footer,
  className,
  ...rest
}: {
  padded?: boolean;
  padding?: 'sm' | 'md' | 'lg' | 'none';
  header?: ReactNode;
  footer?: ReactNode;
} & HTMLAttributes<HTMLDivElement>) {
  const padClass =
    !padded || padding === 'none'
      ? 'cev-card--pad-none'
      : padding === 'sm'
        ? 'cev-card--pad-sm'
        : padding === 'lg'
          ? 'cev-card--pad-lg'
          : 'cev-card--pad-md';
  return (
    <div {...rest} className={cx('cev-card', padClass, className)}>
      {header && <div className="cev-card__header">{header}</div>}
      {children}
      {footer && <div className="cev-card__footer">{footer}</div>}
    </div>
  );
}

// ── Field + Input ───────────────────────────────────────────────────────────

export function Input({
  invalid,
  className,
  disabled,
  readOnly,
  id,
  required,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  ...rest
}: { invalid?: boolean } & InputHTMLAttributes<HTMLInputElement>) {
  const fieldProps = useFieldControlProps({
    id,
    invalid,
    disabled,
    readOnly,
    required,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    'aria-required': ariaRequired,
  });
  const isInvalid = fieldProps.invalid ?? invalid;
  const isDisabled = fieldProps.disabled ?? disabled;
  const isReadOnly = fieldProps.readOnly ?? readOnly;

  return (
    <input
      {...rest}
      id={fieldProps.id}
      required={fieldProps.required}
      disabled={isDisabled}
      readOnly={isReadOnly}
      aria-describedby={fieldProps['aria-describedby']}
      aria-invalid={fieldProps['aria-invalid']}
      aria-required={fieldProps['aria-required']}
      className={cx('cev-input', isInvalid && 'cev-input--invalid', className)}
    />
  );
}
Input.displayName = 'cevopInput';

type CurrencyInputProps = {
  invalid?: boolean;
  /** ISO 4217 currency code. NGN shows ₦; other codes use their symbol or the code itself. */
  currency?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

export function CurrencyInput({
  invalid,
  currency = 'NGN',
  id,
  required,
  disabled,
  readOnly,
  value,
  defaultValue,
  onChange,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  ...rest
}: CurrencyInputProps) {
  const symbol = currencySymbol(currency);
  const currencyName = currencyDisplayName(currency);
  const currencyLabelId = useId();

  return (
    <div className="cev-currency-input">
      <span aria-hidden className="cev-currency-input__symbol">
        {symbol}
      </span>
      <span id={currencyLabelId} className="sr-only">
        {currencyName}
      </span>
      <Input
        {...rest}
        invalid={invalid}
        id={id}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        aria-describedby={[currencyLabelId, ariaDescribedBy].filter(Boolean).join(' ') || undefined}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        inputMode="decimal"
        value={value === undefined ? undefined : formatAmountInput(String(value))}
        defaultValue={defaultValue === undefined ? undefined : formatAmountInput(String(defaultValue))}
        onChange={(event) => {
          const formatted = formatAmountInput(event.target.value);
          if (event.target.value !== formatted) {
            event.target.value = formatted;
          }
          onChange?.(event);
        }}
      />
    </div>
  );
}
CurrencyInput.displayName = 'cevopCurrencyInput';

/** Money amount field — CurrencyInput with org currency. Prefer this for Business/POS amounts. */
export function MoneyInput(props: CurrencyInputProps) {
  return <CurrencyInput {...props} />;
}
MoneyInput.displayName = 'cevopCurrencyInput';

export function QuantityInput({
  invalid,
  id,
  required,
  disabled,
  readOnly,
  value,
  defaultValue,
  onChange,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  ...rest
}: { invalid?: boolean } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  return (
    <Input
      {...rest}
      invalid={invalid}
      id={id}
      required={required}
      disabled={disabled}
      readOnly={readOnly}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      aria-required={ariaRequired}
      inputMode="decimal"
      value={value === undefined ? undefined : formatQuantityInput(String(value))}
      defaultValue={defaultValue === undefined ? undefined : formatQuantityInput(String(defaultValue))}
      onChange={(event) => {
        const formatted = formatQuantityInput(event.target.value);
        if (event.target.value !== formatted) {
          event.target.value = formatted;
        }
        onChange?.(event);
      }}
    />
  );
}
QuantityInput.displayName = 'cevopQuantityInput';

export function PercentageInput({
  invalid,
  id,
  required,
  disabled,
  readOnly,
  value,
  defaultValue,
  onChange,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  ...rest
}: { invalid?: boolean } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  return (
    <div className="cev-currency-input">
      <Input
        {...rest}
        invalid={invalid}
        id={id}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        inputMode="decimal"
        value={value === undefined ? undefined : formatPercentageInput(String(value))}
        defaultValue={defaultValue === undefined ? undefined : formatPercentageInput(String(defaultValue))}
        onChange={(event) => {
          const formatted = formatPercentageInput(event.target.value);
          if (event.target.value !== formatted) {
            event.target.value = formatted;
          }
          onChange?.(event);
        }}
      />
      <span aria-hidden className="cev-currency-input__suffix">
        %
      </span>
    </div>
  );
}
PercentageInput.displayName = 'cevopPercentageInput';

export function Select({
  invalid,
  className,
  children,
  disabled,
  id,
  required,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  ...rest
}: { invalid?: boolean } & SelectHTMLAttributes<HTMLSelectElement>) {
  const fieldProps = useFieldControlProps({
    id,
    invalid,
    disabled,
    required,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    'aria-required': ariaRequired,
  });
  const isInvalid = fieldProps.invalid ?? invalid;
  const isDisabled = fieldProps.disabled ?? disabled;

  return (
    <select
      {...rest}
      id={fieldProps.id}
      required={fieldProps.required}
      disabled={isDisabled}
      aria-describedby={fieldProps['aria-describedby']}
      aria-invalid={fieldProps['aria-invalid']}
      aria-required={fieldProps['aria-required']}
      className={cx('cev-select', isInvalid && 'cev-select--invalid', className)}
    >
      {children}
    </select>
  );
}
Select.displayName = 'cevopSelect';

// ── Segmented control ───────────────────────────────────────────────────────
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  accent,
  'aria-label': ariaLabel,
}: {
  options: { value: T; label: string; color?: string }[];
  value: T;
  onChange: (v: T) => void;
  accent?: string;
  'aria-label'?: string;
}) {
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );

  const moveSelection = (direction: 1 | -1) => {
    const nextIndex = (selectedIndex + direction + options.length) % options.length;
    onChange(options[nextIndex]!.value);
  };

  const onListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      moveSelection(1);
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      moveSelection(-1);
    }
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="cev-segmented"
      style={{ '--cev-segmented-cols': options.length } as CSSProperties}
      onKeyDown={onListKeyDown}
    >
      {options.map((o) => {
        const on = o.value === value;
        const override = o.color ?? accent;
        const customAccent = Boolean(override);
        const optionStyle = customAccent
          ? ({
              '--cev-segmented-accent': override,
              '--cev-segmented-ink': 'var(--ink-inv)',
            } as CSSProperties)
          : undefined;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            className={cx(
              'cev-segmented__option',
              on && 'cev-segmented__option--selected',
              customAccent && 'cev-segmented__option--custom-accent',
            )}
            style={optionStyle}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Modal (single shared overlay primitive) ─────────────────────────────────
// Every modal / sheet / confirmation / popover MUST render through this.
// - Portals to document.body (never inline near the trigger)
// - Centers on the viewport (phone + tablet + desktop — one rule, no bottom sheets)
// - Backdrop click, Escape, focus trap, and body scroll-lock are always on
export type ModalPlacement = 'center' | 'left';

export function Modal({
  title,
  description,
  onClose,
  children,
  footer,
  placement = 'center',
  wide = false,
}: {
  title: string;
  /** Optional accessible description below the title (center placement). */
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** `center` = content dialog (default). `left` = navigation drawer. */
  placement?: ModalPlacement;
  /** Wider centered dialog for multi-field forms and wizards. */
  wide?: boolean;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const isCenter = placement === 'center';

  useEffect(() => acquireScrollLock(), []);
  useRestoreFocus(true);
  useDialogKeyboard(panelRef, onClose);

  if (typeof document === 'undefined') return null;

  const panel = (
    <div
      onClick={onClose}
      data-modal-backdrop=""
      className={cx('cev-modal-backdrop', isCenter ? 'cev-modal-backdrop--center' : 'cev-modal-backdrop--left')}
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description && isCenter ? descriptionId : undefined}
        tabIndex={-1}
        data-modal-placement={placement}
        className={cx(
          'cev-modal',
          isCenter ? 'cev-modal--center' : 'cev-modal--left',
          wide && isCenter && 'cev-modal--wide',
          Boolean(footer) && isCenter && 'cev-modal--with-footer',
        )}
      >
        <div className="cev-modal__header">
          <h2 id={titleId} className={isCenter ? 'cev-modal__title' : 'sr-only'}>
            {title}
          </h2>
          <button
            type="button"
            className="cev-overlay-close cev-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon name="close" size={18} />
          </button>
        </div>
        {description && isCenter ? (
          <div id={descriptionId} className="cev-modal__description">
            {description}
          </div>
        ) : null}
        <div className="cev-modal__body">{children}</div>
        {footer && isCenter ? <div className="cev-modal__footer">{footer}</div> : null}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
